import type { ReactNode } from 'react';

/**
 * Shared building blocks for labelled form controls (Input, Select, Textarea).
 *
 * Centralising the label / hint / error markup guarantees every control gets the
 * same accessible wiring (spec 08 §7) and the same both-theme styling, so no
 * screen builds a bespoke variant (spec 08 §5).
 */

/** Props shared by every labelled form control. */
export interface FieldChrome {
  /** Visible, associated label. */
  label: string;
  /** Field-level validation error; when set the control is marked invalid. */
  error?: string | undefined;
  /** Supporting hint (purpose, format, example). */
  hint?: string | undefined;
  /** Optional trailing slot beside the label (e.g. a HelpTip). */
  labelAddon?: ReactNode;
  /** Whether the field is required (adds a visual + semantic marker). */
  required?: boolean | undefined;
}

/** Label row with an optional required marker and trailing addon. */
export function FieldLabel({
  htmlFor,
  label,
  required,
  addon,
  id,
}: {
  htmlFor: string;
  label: string;
  required?: boolean | undefined;
  addon?: ReactNode;
  /** Optional element id, so a group (e.g. a radiogroup) can `aria-labelledby` it. */
  id?: string | undefined;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-2">
      <label htmlFor={htmlFor} id={id} className="text-sm font-medium text-slate-800 dark:text-slate-200">
        {label}
        {required ? (
          <span className="text-accent-500" aria-hidden="true">
            {' '}
            *
          </span>
        ) : null}
      </label>
      {addon}
    </div>
  );
}

/** Supporting hint text, linked to the control via `aria-describedby`. */
export function FieldHint({ id, children }: { id: string; children: ReactNode }): JSX.Element {
  return (
    <p id={id} className="text-xs text-slate-500 dark:text-slate-400">
      {children}
    </p>
  );
}

/** Error message with a reinforcing icon (text + icon, never colour alone). */
export function FieldError({ id, children }: { id: string; children: ReactNode }): JSX.Element {
  return (
    <p
      id={id}
      className="flex items-center gap-1.5 text-xs font-medium text-accent-600 dark:text-accent-400"
    >
      <svg
        width={13}
        height={13}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
        className="shrink-0"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 8v4" />
        <path d="M12 16h.01" />
      </svg>
      <span>{children}</span>
    </p>
  );
}
